import * as React from 'react';
import {useEffect, useState} from 'react';

import {Facet, FacetField} from '../search.types';
import {FacetLine} from './FacetLine';
import {FilterInput} from '../../ui/forms/FilterInput/FilterInput';
import {ToggleSection} from './ToggleSection';
import {URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import Button from '../../ui/Buttons/Button';

interface FacetSectionProps {
    facetField: FacetField;
    fieldIndex: number;
    urlBuilder: URLBuilder;
}

const MAX_TRUNCATED_TEXT = 7;
const MAX_TRUNCATED_TILE = 12;
const MAX_TRUNCATED = 9;

const getMax = (type: string) => {
    if (type === 'text') {
        return MAX_TRUNCATED_TEXT;
    }
    if (type === 'tile') {
        return MAX_TRUNCATED_TILE;
    }
    return MAX_TRUNCATED;
};

export const FacetSection = ({facetField, fieldIndex, urlBuilder}: FacetSectionProps) => {
    const _maxTruncated = getMax(facetField.leftNavRender);
    const [currentFacets, setCurrentFacets] = useState(facetField.facets.slice(0, _maxTruncated));
    const [isTruncated, setIsTruncated] = useState(true);
    const [searchString, setSearchString] = useState('');
    const [shouldTruncate, setShouldTruncate] = useState(facetField.facets.length > _maxTruncated);

    const isLastChecked = (facet: Facet, facetIndex: number, facetsToCheck: Facet[]) => {
        if (!facet.checked) {
            return false;
        }
        return facetsToCheck[facetIndex + 1] && !facetsToCheck[facetIndex + 1].checked;
    };

    useEffect(() => {
        if (isTruncated) {
            setCurrentFacets(facetField.facets.slice(0, _maxTruncated));
        } else {
            setCurrentFacets(facetField.facets);
        }
    }, [facetField.facets, isTruncated, _maxTruncated]);
    useEffect(() => {
        if (!searchString) {
            setShouldTruncate(facetField.facets.length > _maxTruncated);
            setCurrentFacets(facetField.facets.slice(0, _maxTruncated));
            return;
        }
        const lowerString = searchString.toLowerCase();
        setCurrentFacets(
            facetField.facets.filter((facet) => {
                return facet.text.trim().toLowerCase().includes(lowerString);
            }),
        );
        setShouldTruncate(false);
    }, [facetField.facets, searchString, _maxTruncated]);

    /**
     * Template
     */
    if (facetField.hidden || !(facetField.facets?.length > 0)) {
        return null;
    }
    return (
        <div className={`facetType__${facetField.type} tw-border-t`}>
            <ToggleSection title={facetField.name}>
                <ul
                    className="!tw-p-0"
                    id={`Facet_${fieldIndex}`}
                >
                    {facetField.inlineAttrImagePath && (
                        <li
                            className={`tw-flex tw-list-none tw-justify-center ${
                                facetField.type === 'VisualAttribute'
                                    ? 'tw-border-t-0 tw-align-top tw-inline-block tw-w-[31.5%] tw-my-0 tw-mx-[0.75%] hover:tw-bg-white [&_.hide]:tw-hidden [&_.seeMore]:tw-w-full [&_.seeMore]:tw-py-4 [&_.seeMore]:tw-pr-2.5 hover:[&_.seeMore]:tw-bg-[f5f5f5]'
                                    : `${facetField.type === 'facet__VA_checked' ? 'tw-border-2 tw-border-[#00599c]' : ''}`
                            }`}
                        >
                            <img
                                alt={facetField.name}
                                className="tw-m-2"
                                height="100"
                                src={`/ProductImageThumbs120${facetField.inlineAttrImagePath}`}
                                width="100"
                            />
                        </li>
                    )}
                    {facetField.facets.length > 10 && !(facetField.leftNavRender && facetField.leftNavRender === 'tile') && (
                        <li
                            className={`!tw-w-full tw-list-none ${
                                facetField.type === 'VisualAttribute'
                                    ? 'tw-border-t-0 tw-align-top tw-inline-block tw-w-[31.5%] tw-my-0 tw-mx-[0.75%] hover:tw-bg-white [&_.hide]:tw-hidden [&_.seeMore]:tw-w-full [&_.seeMore]:tw-py-4 [&_.seeMore]:tw-pr-2.5 hover:[&_.seeMore]:tw-bg-[f5f5f5]'
                                    : `${facetField.type === 'facet__VA_checked' ? 'tw-border-2 tw-border-[#00599c]' : ''}`
                            }`}
                        >
                            <div className="tw-mt-1 tw-mb-4">
                                <FilterInput
                                    onInput={(input) => {
                                        setSearchString(input);
                                    }}
                                />
                            </div>
                        </li>
                    )}
                    <div>
                        {currentFacets.map((facet, facetIndex) => (
                            <FacetLine
                                facet={facet}
                                facetField={facetField}
                                facetIndex={facetIndex}
                                isLastChecked={isLastChecked(facet, facetIndex, currentFacets)}
                                key={facetIndex}
                                urlBuilder={urlBuilder}
                            />
                        ))}
                    </div>
                    {shouldTruncate && (
                        <li
                            className={`!tw-mt-5 tw-cursor-pointer tw-list-none !tw-w-full ${
                                facetField.type === 'VisualAttribute'
                                    ? 'tw-border-t-0 tw-align-top tw-inline-block tw-w-[31.5%] tw-my-0 tw-mx-[0.75%] hover:tw-bg-white [&_.hide]:tw-hidden [&_.seeMore]:tw-w-full [&_.seeMore]:tw-py-4 [&_.seeMore]:tw-pr-2.5 hover:[&_.seeMore]:tw-bg-[f5f5f5]'
                                    : `${facetField.type === 'facet__VA_checked' ? 'tw-border-2 tw-border-[#00599c]' : ''}`
                            }`}
                        >
                            <Button
                                className="!tw-m-0 !tw-p-0 hover:!tw-underline"
                                onClick={() => {
                                    setIsTruncated(!isTruncated);
                                }}
                                size="sm"
                                variant="link"
                            >
                                {isTruncated ? `SHOW MORE` : `SHOW LESS`}
                            </Button>
                        </li>
                    )}
                    {currentFacets.length === 0 && <li className="!tw-p-3 tw-text-center !tw-w-full tw-text-base">No matches found.</li>}
                </ul>
            </ToggleSection>
        </div>
    );
};
